/*
 * Copyright (c) 2020. American Express Company
 */

import { LocalDate } from '@js-joda/core';
import BWDataService from './bw-data.service';
import { IncomeSourceModel } from 'src/types/income-source.model';
import { FullUserModel } from 'src/types/user.model';
import CardFactory from 'src/bw/factories/card.factory';

export interface RetireCalculatorResponse {
    ageDead: number;
    ageBroke?: number;
    retirementAssets: number;
    retirementDeficit: number;
}

export default class RetireService extends BWDataService {
    /**
     * Get the IncomeSources on the given date, sorted by annualised income
     * @param date on which it fetch the amount of each income
     * @param withoutCardIds optional list of card id's to ignore, to give the income without the effect of those cards
     */
    public static async getIncomeSources(date: LocalDate, withoutCardIds: number[] = []): Promise<IncomeSourceModel[]> {
        const dateString: string = date.withDayOfMonth(1).toString();
        const withoutCards: string = withoutCardIds.join(',');
        const response: IncomeSourceModel[] = await this.get(`/incomes/month/${dateString}/${withoutCards}`);

        return response?.sort((a: IncomeSourceModel, b: IncomeSourceModel) => b.annualizedIncome - a.annualizedIncome);
    }

    /**
     * Return income sources that are endable on the given date, but that aren't being ended anywhere in the timeline
     * @param user containing existing events
     * @param date on which it fetch the amount of each income
     */
    public static async getNonEndingIncomeSources(user: FullUserModel, date: LocalDate): Promise<IncomeSourceModel[]> {
        const endedIncomes = user.cards
            .filter((card) => CardFactory.isJobEndingCard(card))
            .flatMap((card) => card.modifyIncomes);
        try {
            const incomeSources: IncomeSourceModel[] = await RetireService.getIncomeSources(date);
            return incomeSources?.filter((income) => !endedIncomes.includes(income.incomeUid));
        } catch (e) {
            console.error('Failed to fetch incomes:', e);
            throw e;
        }
    }

    public static async retrieveCalculation(args: {
        cardId: number;
        startDate: LocalDate;
        incomeUid: string;
        annualExpenses: number;
    }): Promise<RetireCalculatorResponse> {
        try {
            return await this.post('/retireCalculator', args);
        } catch (e) {
            console.error('Failed to calculate retirement values:', e);
            throw e;
        }
    }
}
